import React from 'react';
import { useFilter } from 'react-aria';
import { Autocomplete as AriaAutocomplete } from 'react-aria-components';

export interface AutocompleteProps {
  /** A SearchField, then the ListBox it drives. */
  children: React.ReactNode;
  /**
   * The typed text (controlled). With it, the caller narrows: pass the
   * matches as the list's `items`, as a server search does. Without it, the
   * list narrows itself to the options whose text contains what is typed.
   */
  inputValue?: string;
  /** Called with the text on every keystroke. */
  onInputChange?: (text: string) => void;
}

/**
 * A search that drives a list while keeping the focus: typing narrows the
 * list, the arrows move a highlight through it, Enter picks the highlighted
 * option, and focus never leaves the search, so typing again narrows again
 * (picker.md). The filterable Select's head and the link modal's search are
 * this composition.
 *
 * Who narrows follows who owns the text, as in Combobox: no filter option.
 */
export const Autocomplete = ({ children, inputValue, onInputChange }: AutocompleteProps) => {
  const { contains } = useFilter({ sensitivity: 'base' });
  if (inputValue === undefined) {
    return (
      <AriaAutocomplete filter={contains} onInputChange={onInputChange}>
        {children}
      </AriaAutocomplete>
    );
  }
  /* The caller's results can lag the text, so typing highlights nothing: Enter waits for an arrow
   * rather than pick an option the last search left on screen. */
  return (
    <AriaAutocomplete inputValue={inputValue} onInputChange={onInputChange} disableAutoFocusFirst>
      {children}
    </AriaAutocomplete>
  );
};
